// Renders the compiled report PDF (from a presigned URL) to scrollable canvas
// pages. Ports the proven pdf.js setup from study/pdf-marking-workspace.tsx (the
// `?url` worker import + top-level workerSrc, page loop, DPR cap, RenderTask
// cancellation) — minus the text layer / marks / snip overlays.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { Spinner } from "@/components/ui";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

const DPR_CAP = 2;

export function PdfPreviewPanel({ url }: { url: string | null }) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;
    const tasks: RenderTask[] = [];
    const container = containerRef.current;

    async function render() {
      if (!container) return;
      setLoading(true);
      setError(null);
      // Preserve scroll across a recompile re-render.
      const prevScroll = container.scrollTop;
      try {
        doc = await pdfjsLib.getDocument({ url: url! }).promise;
        if (cancelled) return;
        container.innerHTML = "";
        const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
        const fitWidth = Math.max(1, container.clientWidth - 24);
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          // Bake DPR into the render scale (mirrors the study workspace); the CSS
          // size divides it back out so the page lays out at `fitWidth`.
          const viewport = page.getViewport({ scale: (fitWidth * dpr) / base.width });
          const canvas = document.createElement("canvas");
          canvas.className = "mx-auto mb-3 rounded-lg border border-black/10 shadow-sm bg-white";
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${fitWidth}px`;
          canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
          const ctx = canvas.getContext("2d");
          if (!ctx) continue;
          container.appendChild(canvas);
          const task = page.render({ canvasContext: ctx, viewport });
          tasks.push(task);
          await task.promise.catch(() => {});
        }
        container.scrollTop = prevScroll;
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to render PDF");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    render();
    return () => {
      cancelled = true;
      tasks.forEach((tk) => tk.cancel());
      doc?.destroy();
    };
  }, [url]);

  if (!url) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-sm text-ink-faint">
        {t("No compiled PDF yet. Edit the LaTeX and press Compile.")}
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {loading && (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs shadow">
          <Spinner className="h-3 w-3" /> {t("Rendering…")}
        </div>
      )}
      {error && (
        <div className="p-4 text-sm text-rose-600">{t("Could not render the PDF.")} {error}</div>
      )}
      <div ref={containerRef} className="h-full overflow-auto bg-zinc-100 p-3" />
    </div>
  );
}
